/**
 * Shared signup form for the unified auth-flow-redesign surface. Used
 * by both the mobile signup screen (`/(auth)/signup`) and the web
 * public deal-landing page (`/[slug]`). Form fields + validation +
 * submit are identical; outcome handling is centralized here so both
 * surfaces stay in sync.
 *
 * Outcome handling (locked from auth-flow-redesign plan):
 *   - new_deal_anchored → Linking.openURL(redirect_url) by default;
 *     parents can override via `onDealAnchored` to render a success
 *     card with a user-gesture-driven CTA instead.
 *   - new_generic → router.push('/(auth)/verify', { contact, channel })
 *   - existing → router.push('/(auth)/login', { prefill_contact, banner })
 *
 * `pendingReferral` is cleared synchronously in the response handler,
 * before navigation, on every outcome (replaces today's "clear at PIN
 * verify only" rule which left stale referrals stranded after
 * deal-anchored submits that backgrounded the app mid-flight).
 */
import { router } from 'expo-router';
import { type ReactNode, useState } from 'react';
import { Linking, StyleSheet, Text, View } from 'react-native';

import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { colors, fonts, fontSizes, spacing } from '@/constants/theme';
import { formatUsPhoneDisplay, isValidEmail, normalizeUsPhone } from '@/lib/contact';
import { clearPendingReferral } from '@/lib/pendingReferral';
import { trackLead } from '@/services/metaPixel';
import {
  publicSignupService,
  type PublicSignupDeal,
  type PublicSignupResponse,
} from '@/services/publicSignup';

export interface SignupFormProps {
  /** `?r=<token>` — referral link token captured from the inbound URL. */
  referralToken?: string;
  /** Legacy `?ref=<ref_id>` — older share URLs. */
  refId?: string;
  /**
   * Back-compat: when the form lives on `app.marketpryce.com/<slug>`
   * the slug is forwarded to the server. Server-side `resolved_deal`
   * has the link winning over the slug if both are present.
   */
  pathSlug?: string;
  /** Submit-button label. Defaults to "Continue". */
  submitLabel?: string;
  /** Rendered directly under the submit button, above the fineprint (e.g. social proof). */
  afterSubmit?: ReactNode;
  /**
   * Called when the server returns `outcome: "new_deal_anchored"`.
   * Default behavior is `Linking.openURL(redirectUrl)`. Parents may
   * override to render a success card instead (web public landing
   * preserves the "Open SoFi" gesture-driven CTA this way).
   */
  onDealAnchored?: (deal: PublicSignupDeal, redirectUrl: string | null) => void;
}

export function SignupForm({
  referralToken,
  refId,
  pathSlug,
  submitLabel = 'Continue',
  afterSubmit,
  onDealAnchored,
}: SignupFormProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneRaw, setPhoneRaw] = useState('');
  const [email, setEmail] = useState('');
  const [phoneFocused, setPhoneFocused] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [firstFocused, setFirstFocused] = useState(false);
  const [lastFocused, setLastFocused] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isSubmittable =
    !submitting &&
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    normalizeUsPhone(phoneRaw) !== null &&
    isValidEmail(email);

  async function handleSubmit() {
    if (!isSubmittable) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const phoneE164 = normalizeUsPhone(phoneRaw);
      if (!phoneE164) {
        setSubmitError('That phone number looks invalid. Please double-check.');
        return;
      }

      const response = await publicSignupService.signup({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phoneE164,
        email: email.trim(),
        r: referralToken,
        ref_id: refId,
        deal_slug: pathSlug,
      });

      // Meta Pixel Lead event — fires once server accepted the signup
      // request, regardless of outcome (new vs existing). The
      // CompleteRegistration event fires later after PIN verify.
      trackLead({ email: email.trim(), phone: phoneE164 });

      // Clear pendingReferral synchronously, before any navigation, on
      // every outcome. The user is now created (or recognized) — the
      // referral context has done its job. Locked from eng review.
      await clearPendingReferral().catch((err) => {
        if (__DEV__) console.warn('[signup] failed to clear pendingReferral', err);
      });

      handleOutcome(response, phoneE164);
    } catch (err) {
      const fallback = 'Could not sign you up. Please try again.';
      const message = err instanceof Error && err.message ? err.message : fallback;
      if (__DEV__) console.warn('[signup] submit failed', err);
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  }

  function handleOutcome(response: PublicSignupResponse, phoneE164: string) {
    switch (response.outcome) {
      case 'new_deal_anchored':
        if (!response.deal) {
          // Server said deal-anchored but didn't include the data the
          // success screen needs. Don't silently fall through to "no
          // navigation, no error" — surface it so the user isn't stuck.
          setSubmitError('Signup succeeded but the deal redirect was missing. Please try again.');
          return;
        }
        // Full deal: the account was created but there's intentionally no
        // redirect (out of monthly spots). That's a success, not the
        // "redirect missing" error. Branch on `full` INDEPENDENT of the
        // callback so a caller without onDealAnchored can never mislabel a
        // successful full signup as a redirect error.
        if (response.deal.full) {
          if (onDealAnchored) onDealAnchored(response.deal, null);
          return;
        }
        if (!response.redirect_url) {
          setSubmitError('Signup succeeded but the deal redirect was missing. Please try again.');
          return;
        }
        if (onDealAnchored) {
          onDealAnchored(response.deal, response.redirect_url);
        } else {
          void Linking.openURL(response.redirect_url);
        }
        return;
      case 'new_generic':
        router.push({
          pathname: '/(auth)/verify',
          params: { contact: phoneE164, channel: 'sms' },
        });
        return;
      case 'existing':
        // From a deal landing, carry the deal detail as the post-PIN
        // destination so the recognized user returns to that deal
        // instead of home.
        router.push({
          pathname: '/(auth)/login',
          params: pathSlug
            ? {
                prefill_contact: phoneE164,
                banner: 'existing_account',
                redirect: `/deals/${pathSlug}`,
              }
            : { prefill_contact: phoneE164, banner: 'existing_account' },
        });
        return;
      default: {
        // Runtime guard. The TS union is exhaustive, but a stale bundle
        // hitting a newer server (or vice versa) can deliver an outcome
        // we don't recognize. Don't silently no-op — show the user
        // something actionable instead of leaving the submit button
        // spinning back to idle.
        if (__DEV__) {
          console.error('[signup] unrecognized outcome', response.outcome, response);
        }
        setSubmitError(
          'Got an unexpected response from the server. Please update the app or try again.',
        );
        return;
      }
    }
  }

  return (
    <View style={styles.formWrap}>
      <View style={styles.nameRow}>
        <Input
          id="signup-first-name"
          label="First Name"
          size="compact"
          style={styles.nameField}
          focused={firstFocused}
          value={firstName}
          onChangeText={setFirstName}
          onFocus={() => setFirstFocused(true)}
          onBlur={() => setFirstFocused(false)}
          placeholder="Joe"
          autoCapitalize="words"
          autoComplete="given-name"
          textContentType="givenName"
          returnKeyType="next"
        />
        <Input
          id="signup-last-name"
          label="Last Name"
          size="compact"
          style={styles.nameField}
          focused={lastFocused}
          value={lastName}
          onChangeText={setLastName}
          onFocus={() => setLastFocused(true)}
          onBlur={() => setLastFocused(false)}
          placeholder="Smith"
          autoCapitalize="words"
          autoComplete="family-name"
          textContentType="familyName"
          returnKeyType="next"
        />
      </View>
      <Input
        id="signup-phone"
        label="Phone Number"
        size="compact"
        focused={phoneFocused}
        value={formatUsPhoneDisplay(phoneRaw)}
        onChangeText={setPhoneRaw}
        onFocus={() => setPhoneFocused(true)}
        onBlur={() => setPhoneFocused(false)}
        placeholder="(555) 123-4567"
        keyboardType="phone-pad"
        autoComplete="tel"
        textContentType="telephoneNumber"
        maxLength={14}
      />
      <Input
        id="signup-email"
        label="Email"
        size="compact"
        focused={emailFocused}
        value={email}
        onChangeText={setEmail}
        onFocus={() => setEmailFocused(true)}
        onBlur={() => setEmailFocused(false)}
        placeholder="you@school.edu"
        keyboardType="email-address"
        autoComplete="email"
        textContentType="emailAddress"
        autoCapitalize="none"
        autoCorrect={false}
      />

      {submitError && <Text style={styles.error}>{submitError}</Text>}

      <Button
        label={submitLabel}
        variant="primary"
        fullWidth
        disabled={!isSubmittable}
        loading={submitting}
        onPress={handleSubmit}
      />

      {afterSubmit}

      <Text style={styles.fineprint}>
        By continuing, you agree to MarketPryce&apos;s terms and consent to receive text messages from MarketPryce and the athlete who referred you. We&apos;ll use your email to confirm your sign-up.
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  formWrap: {
    // The card container uses alignItems:'center', which leaves this
    // wrapper at an indefinite width. Inputs still fill via stretch, but
    // the submit button's fullWidth (width:'100%') needs a definite
    // parent width to resolve against — without this it collapses to
    // content width and left-aligns. See Button fullWidth.
    width: '100%',
    gap: spacing.base,
  },
  // First + Last side by side (per the 2026-08 landing mockup). Each field
  // takes half; the row's gap matches the vertical field gap so the grid
  // reads evenly.
  nameRow: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  nameField: {
    flex: 1,
    minWidth: 0,
  },
  error: {
    fontFamily: fonts.bodyMedium,
    fontSize: fontSizes.sm,
    color: colors.danger,
  },
  fineprint: {
    fontFamily: fonts.body,
    fontSize: fontSizes.xs,
    color: colors.textMuted,
    lineHeight: 18,
    textAlign: 'center',
  },
});
